import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { RULES } from "@/constants/rulesTestIds";
import { LIFECYCLE_LABELS, ruleTypeLabel, severityLabel } from "@/constants/rulesVocab";

export default function RuleDetail() {
  const { brandId, ruleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rule, setRule] = useState(null);
  const [docs, setDocs] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: r }, dvsResult, conflictsResult] = await Promise.all([
        api.get(`/brands/${brandId}/rules/${ruleId}`),
        api.get(`/brands/${brandId}/document-versions`).catch((e) => {
          // eslint-disable-next-line no-console
          console.warn("[rule-detail] doc-versions load failed", e?.response?.status, e?.response?.data);
          return { data: [] };
        }),
        api.get(`/brands/${brandId}/rules/${ruleId}/conflicts`).catch((e) => {
          // eslint-disable-next-line no-console
          console.warn("[rule-detail] conflicts load failed", e?.response?.status, e?.response?.data);
          return { data: { conflicts: [] } };
        }),
      ]);
      const dvs = dvsResult.data || [];
      setRule(r);
      setDocs(dvs);
      setConflicts(conflictsResult.data?.conflicts || []);
      const ref = dvs.find((d) => d.is_reference);
      setSelectedDoc(ref ? ref.document_version_id : (dvs[0]?.document_version_id || ""));
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // eslint-disable-next-line no-console
      console.error("[rule-detail] rule load failed", status, detail);
      setError(status === 404 ? "Rule not found" : (detail || `Could not load rule (HTTP ${status || "—"})`));
    }
  };

  useEffect(() => { load(); }, [ruleId]);

  const runTest = async () => {
    if (!selectedDoc) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/brands/${brandId}/rules/${ruleId}/test`, {
        document_version_id: selectedDoc,
      });
      setTestResult(data);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Test failed");
    } finally {
      setBusy(false);
    }
  };

  const activate = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/brands/${brandId}/rules/${ruleId}/activate`);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not activate");
    } finally {
      setBusy(false);
    }
  };

  const retire = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/brands/${brandId}/rules/${ruleId}/retire`);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not retire");
    } finally {
      setBusy(false);
    }
  };

  if (error && !rule) return <p className="text-[#B5462F]">{error}</p>;
  if (!rule) return <p className="text-[#6B6358]">Loading&hellip;</p>;

  const canActivate = rule.lifecycle_status === "draft" && (rule.test_status?.tested_against?.length || 0) > 0;
  const canManage = user?.is_platform_admin;

  return (
    <div data-testid={RULES.detailRoot} className="max-w-5xl">
      <button onClick={() => navigate(`/brands/${brandId}/rules`)} className="text-xs text-[#6B6358] hover:text-[#1A1F2C] mb-6">
        &larr; All rules
      </button>

      <div className="flex items-start justify-between mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-[#B5462F] mb-3">
            {ruleTypeLabel(rule.rule_type)} &middot; Tier {rule.tier || 1}
          </p>
          <h1 className="font-serif text-4xl tracking-tight leading-tight">{rule.explanation}</h1>
        </div>
        <StatusPill status={rule.lifecycle_status} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        <Meta label="Severity" value={severityLabel(rule.severity)} />
        <Meta label="Version" value={`v${rule.audit?.version || 1}`} />
        <Meta label="Last tested" value={rule.test_status?.last_test_summary || "Never"} />
      </div>

      <Section title="Source text">
        <p className="font-serif text-lg text-[#3D3A35] leading-relaxed italic">
          &ldquo;{rule.source_text}&rdquo;
        </p>
      </Section>

      <Section title="Scope">
        <ScopeView scope={rule.scope} />
      </Section>

      <Section title="Parameters">
        <pre className="text-xs font-mono bg-[#F8F5EE] border border-[#1A1F2C]/10 rounded-md p-4 overflow-auto">
          {JSON.stringify(rule.parameters, null, 2)}
        </pre>
      </Section>

      <Section title="Test against a document">
        <div className="flex items-center gap-3 mb-4">
          <select
            data-testid={RULES.detailTestDocSelect}
            value={selectedDoc}
            onChange={(e) => setSelectedDoc(e.target.value)}
            className="px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE] text-sm"
          >
            {docs.map((d) => (
              <option key={d.document_version_id} value={d.document_version_id}>
                {d.name} {d.is_reference ? "· Reference" : ""}
              </option>
            ))}
          </select>
          <button
            data-testid={RULES.detailTestButton}
            onClick={runTest}
            disabled={busy || !selectedDoc}
            className="px-4 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm disabled:opacity-50"
          >
            {busy ? "Testing..." : "Test this rule"}
          </button>
        </div>

        {(testResult?.findings?.length || 0) > 0 && (
          <div data-testid={RULES.detailFindings} className="space-y-3 mt-4">
            <p className="text-xs uppercase tracking-[0.18em] text-[#6B6358]">
              Summary: <strong className="text-[#1A1F2C]">{testResult.summary}</strong>
            </p>
            {testResult.findings.map((f, i) => (
              <FindingCard key={i} f={f} />
            ))}
          </div>
        )}
      </Section>

      {conflicts.length > 0 && (
        <Section title={`Potential conflicts · ${conflicts.length}`}>
          <div data-testid={RULES.detailConflicts} className="space-y-2">
            {conflicts.map((c) => (
              <div key={c.rule_id} className="border border-[#B5462F]/30 bg-[#FDF1EC] rounded-md p-3 text-sm">
                <div className="text-xs uppercase tracking-[0.15em] text-[#B5462F] mb-1">
                  {ruleTypeLabel(c.rule_type)} &middot; {severityLabel(c.severity)}
                </div>
                <p className="text-[#3D3A35]">{c.explanation}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {error && <p className="text-sm text-[#B5462F] mt-3">{error}</p>}

      {canManage && (
        <div className="flex items-center gap-3 mt-10 pt-8 border-t border-[#1A1F2C]/10">
          {rule.lifecycle_status === "draft" && (
            <button
              data-testid={RULES.detailActivateButton}
              onClick={activate}
              disabled={!canActivate || busy}
              title={!canActivate ? "Test the rule against at least one document first" : ""}
              className="px-5 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm font-medium disabled:opacity-40"
            >
              Activate
            </button>
          )}
          {rule.lifecycle_status === "active" && (
            <button
              data-testid={RULES.detailRetireButton}
              onClick={retire}
              disabled={busy}
              className="px-5 py-2 border border-[#B5462F] text-[#B5462F] rounded-full text-sm font-medium"
            >
              Retire
            </button>
          )}
          {rule.lifecycle_status === "draft" && (
            <span className="text-xs text-[#6B6358]">
              {canActivate ? "Ready to activate." : "Test against at least one document before activating."}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h3 className="font-serif text-xl mb-3">{title}</h3>
      {children}
    </section>
  );
}

function Meta({ label, value }) {
  return (
    <div className="border border-[#1A1F2C]/10 bg-white rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6358] mb-1">{label}</div>
      <div className="font-serif text-xl">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const colors = {
    draft: "bg-[#F8F5EE] text-[#6B6358] border-[#1A1F2C]/15",
    active: "bg-[#1A1F2C] text-[#F8F5EE] border-[#1A1F2C]",
    retired: "bg-transparent text-[#B5462F] border-[#B5462F]/40",
  };
  return (
    <span className={`inline-block px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.18em] border ${colors[status] || ""}`}>
      {LIFECYCLE_LABELS[status] || status}
    </span>
  );
}

function ScopeView({ scope }) {
  const dims = [
    ["formats", "Formats"], ["panels", "Panels"], ["campaigns", "Campaigns"],
    ["locations", "Locations"], ["location_groups", "Location groups"], ["user_roles", "User roles"],
  ];
  const populated = dims.filter(([k]) => (scope?.[k] || []).length > 0);
  if (populated.length === 0) return <p className="text-sm text-[#6B6358]">Brand-wide (no scope dimensions populated).</p>;
  return (
    <ul className="space-y-1.5 text-sm">
      {populated.map(([k, label]) => (
        <li key={k}>
          <span className="text-[#6B6358]">{label}:</span>{" "}
          <span className="font-mono text-xs">{(scope[k] || []).join(", ")}</span>
        </li>
      ))}
    </ul>
  );
}

function FindingCard({ f }) {
  const colors = {
    pass: "border-[#1A1F2C]/15 bg-white",
    fail: "border-[#B5462F]/40 bg-[#FDF1EC]",
    not_applicable: "border-[#1A1F2C]/10 bg-[#F8F5EE]",
    requires_judgment: "border-[#1A1F2C]/15 bg-white",
  };
  const labels = {
    pass: "Pass", fail: "Fail",
    not_applicable: "N/A", requires_judgment: "Needs judgment",
  };
  return (
    <div className={`border rounded-md p-3 ${colors[f.outcome] || ""}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#1A1F2C]">{labels[f.outcome]}</span>
        {f.panel && <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6358]">· panel: {f.panel}</span>}
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6358] ml-auto italic">
          evaluated at: {f.evaluation_scope.replace("_", " ")}
        </span>
      </div>
      <p className="text-sm text-[#3D3A35]">{f.explanation}</p>
    </div>
  );
}
